/**
 * MasterControlsPill — controles do mestre (bottom-left colapsável).
 *
 * Padrão UX herdado de v2 (ver src/test/rpgia-aula-v3-complement/.../teacher-controls.js):
 *   colapsado: ‹  N/total  ›  ⋯
 *   expandido: label do slide atual + botão "link do aluno"
 *
 * Suporta teclas ← → quando não há input focado.
 */

import { useEffect, useState } from 'react'

import { useAdapter } from '../adapter'

interface Props {
  totalSlides: number
  currentIndex: number
  currentLabel: string | undefined
  /** Reabre o overlay de QR de entrada (atrasados). Botão só aparece se passado. */
  onShowQr?: () => void
}

export function MasterControlsPill({
  totalSlides,
  currentIndex,
  currentLabel,
  onShowQr,
}: Props) {
  const adapter = useAdapter()
  const [expanded, setExpanded] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return
      if (e.key === 'ArrowLeft') adapter.setSlide(Math.max(0, currentIndex - 1))
      if (e.key === 'ArrowRight') adapter.setSlide(Math.min(totalSlides - 1, currentIndex + 1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [adapter, currentIndex, totalSlides])

  function copyStudentLink() {
    const url = new URL(window.location.href)
    url.searchParams.set('role', 'player')
    url.searchParams.delete('adapter')
    navigator.clipboard
      ?.writeText(url.toString())
      .then(() => flash('Link de aluno copiado'))
      .catch(() => flash('Falha ao copiar link'))
  }

  function flash(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2000)
  }

  return (
    <>
      <div
        style={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 8,
          fontFamily: 'var(--font-lab-mono)',
          opacity: expanded ? 1 : 0.75,
          transition: 'opacity 150ms',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = expanded ? '1' : '0.75')}
      >
        {expanded && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              background: 'var(--color-lab-bg-2)',
              border: '1px solid var(--color-lab-rule)',
              borderRadius: 'var(--radius-lab-sm)',
              fontSize: 'var(--text-lab-sm)',
              boxShadow: 'var(--lab-shadow-sm)',
            }}
          >
            <span style={{ color: 'var(--color-lab-ink-3)' }}>
              {currentLabel ?? '—'}
            </span>
            <button
              type="button"
              onClick={copyStudentLink}
              style={btnSecondary}
              aria-label="Copiar link de aluno"
            >
              link do aluno
            </button>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '6px 8px',
            background: 'var(--color-lab-bg-2)',
            border: '1px solid var(--color-lab-rule)',
            borderRadius: 'var(--radius-lab)',
            boxShadow: 'var(--lab-shadow-sm)',
          }}
        >
          <button
            type="button"
            onClick={() => adapter.setSlide(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
            style={btnIcon}
            aria-label="Slide anterior"
          >
            ‹
          </button>
          <div
            aria-live="polite"
            style={{
              minWidth: 56,
              textAlign: 'center',
              fontSize: 'var(--text-lab-sm)',
              fontWeight: 600,
            }}
          >
            <span>{currentIndex + 1}</span>
            <span style={{ color: 'var(--color-lab-ink-3)' }}> / </span>
            <span>{totalSlides}</span>
          </div>
          <button
            type="button"
            onClick={() => adapter.setSlide(Math.min(totalSlides - 1, currentIndex + 1))}
            disabled={currentIndex >= totalSlides - 1}
            style={btnIcon}
            aria-label="Próximo slide"
          >
            ›
          </button>
          {onShowQr && (
            <button
              type="button"
              onClick={onShowQr}
              style={{ ...btnIcon, fontSize: 'var(--text-lab-sm)', fontWeight: 600 }}
              aria-label="Mostrar QR code de entrada"
            >
              QR
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            style={btnIcon}
            aria-label="Mais controles"
          >
            ⋯
          </button>
        </div>
      </div>

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 80,
            left: 16,
            zIndex: 60,
            padding: '8px 14px',
            background: 'var(--color-lab-ink)',
            color: 'var(--lab-ink-inverse)',
            borderRadius: 'var(--radius-lab-sm)',
            fontSize: 'var(--text-lab-sm)',
            fontFamily: 'var(--font-lab-mono)',
            boxShadow: 'var(--lab-shadow)',
          }}
        >
          {toast}
        </div>
      )}
    </>
  )
}

const btnIcon: React.CSSProperties = {
  minWidth: 44,
  minHeight: 44,
  padding: '0 10px',
  background: 'transparent',
  border: 'none',
  borderRadius: 'var(--radius-lab-sm)',
  fontFamily: 'inherit',
  fontSize: 'var(--text-lab-md)',
  cursor: 'pointer',
  color: 'var(--color-lab-ink)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const btnSecondary: React.CSSProperties = {
  minHeight: 32,
  padding: '4px 10px',
  background: 'var(--color-lab-accent)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-lab-sm)',
  fontFamily: 'inherit',
  fontSize: 'var(--text-lab-xs)',
  fontWeight: 600,
  cursor: 'pointer',
}
